import { Button, IconButton, TextInput, Textarea } from '@mint/ui';
import React, { useState } from 'react';
import {
  removeEventOccurrence,
  setEventOccurrenceDuration,
  setEventOccurrenceStartsAt,
} from '../../../actions';
import { AdminEventPanelActionButton } from './admin-event-panel-action-button';
import { AdminEventPanelProps } from './admin-event-panel-props';
import { AdminLinkedPage } from '../admin-linked-page';
import { AdminLinkedPlace } from '../admin-linked-place';
import { AdminPanel } from '../admin-panel';
import { AdminPanelBody } from '../admin-panel-body';
import { AdminPanelHeader } from '../admin-panel-header';
import { AdminPanelHeading } from '../admin-panel-heading';
import { AdminPanelHeadingAction } from '../admin-panel-heading-action';
import { AdminPanelHeadingText } from '../admin-panel-heading-text';
import { AdminPanelHeadingTwoText } from '../admin-panel-heading-two-text';
import { AdminPanelMainForm } from '../admin-panel-main-form';
import { AdminPosterImageUploader } from '../admin-poster-image-uploader';
import { EventOccurrence } from './event-occurrence';
import PropTypes from 'prop-types';
import { RRule } from 'rrule';
import styled from 'styled-components';
import { useAdminEventPanelController } from './use-admin-event-panel-controller';
import { useDispatch } from 'react-redux';

const HeadingWrapper = styled.div`
  display: flex;
  margin-top: 2rem;

  ${AdminPanelHeadingTwoText} {
    flex: 1;
  }

  ${IconButton}, ${Button} {
    align-self: flex-end;
    position: relative;
    top: 2px;
  }
`;

export const AdminEventPanel: React.FunctionComponent<AdminEventPanelProps> = ({
  eventKey,
}): React.ReactElement => {
  const dispatch = useDispatch();
  const [rruleFirst, setRruleFirst] = useState(' ');
  const [rruleText, setRruleText] = useState('');
  const [rruleEndTime, setRruleEndTime] = useState(' ');

  const {
    description,
    name,
    onAddRecurrenceRuleClick,
    onDescriptionChange,
    onNameChange,
    onPosterImageUploadStart,
    onTagsChange,
    posterImageKey,
    placeName,
    onPlaceSelect,
    pageTitle,
    onPageSelect,
    tags,
    occurrences,
    onAddOccurrenceClick,
  } = useAdminEventPanelController(eventKey);

  return (
    <AdminPanel>
      <AdminPanelHeader>
        <AdminPanelHeading>
          <AdminPanelHeadingText>Event</AdminPanelHeadingText>
          <AdminPanelHeadingAction>
            <AdminEventPanelActionButton eventKey={eventKey} />
          </AdminPanelHeadingAction>
        </AdminPanelHeading>
      </AdminPanelHeader>
      <AdminPanelBody>
        <AdminPanelMainForm>
          <TextInput
            onChange={onNameChange}
            value={name}
            label="Name"
            helperText="Event name."
          />
          <Textarea
            onChange={onDescriptionChange}
            label="Description"
            value={description}
            helperText="Description of the event."
          />
          <Textarea
            onChange={onTagsChange}
            label="Tags"
            value={tags}
            helperText="Event tags separated with spaces."
          />
          <AdminPosterImageUploader
            imageKey={posterImageKey}
            onUploadStart={onPosterImageUploadStart}
            helperText="This image is used on cards and for social media shares."
          />
          <AdminLinkedPlace name={placeName} onPlaceSelect={onPlaceSelect} />
          <AdminLinkedPage title={pageTitle} onPageSelect={onPageSelect} />

          <HeadingWrapper>
            <AdminPanelHeadingTwoText>Generate</AdminPanelHeadingTwoText>
          </HeadingWrapper>
          <TextInput
            // defaultValue={startsAt || ' '}
            value={rruleFirst}
            label="First Date and Time"
            onChange={e => setRruleFirst(e.target.value)}
            type="datetime-local"
            helperText={
              <>
                Date and time of first occurrence <b>(required)</b>.
              </>
            }
          />
          <TextInput
            // defaultValue={startsAt || ' '}
            value={rruleEndTime}
            label="End Time"
            onChange={e => setRruleEndTime(e.target.value)}
            type="time"
            helperText={
              <>
                End time for the occurrence <b>(optional)</b>.
              </>
            }
          />
          <TextInput
            onChange={e => setRruleText(e.target.value)}
            value={rruleText}
            label="Recurrence Rules"
            helperText={
              <>
                Generate event occurrences based on text input.
                <br />
                "Every Monday"
                <br />
                "Every Tuesday for 7 times"
                <br />
                "Every Friday until 9/12/24"
              </>
            }
          />
          <Button
            variant="raised"
            style={{ marginTop: '1rem' }}
            onClick={() => {
              try {
                const options = RRule.parseText(rruleText);
                const dtstart = new Date(rruleFirst);
                let duration: number | undefined = undefined;
                if (rruleEndTime.trim()) {
                  const ed = new Date(dtstart);
                  const edt = new Date('1/1/70 ' + rruleEndTime);
                  ed.setHours(edt.getHours());
                  ed.setMinutes(edt.getMinutes());
                  const msDuration = ed.getTime() - dtstart.getTime();
                  duration = Math.max(0, msDuration / 1000);
                  duration = duration > 0 ? duration : undefined;
                }
                const rrule = new RRule({
                  ...options,
                  dtstart,
                });
                onAddRecurrenceRuleClick(rrule.all().slice(0, 50), duration);
              } catch (e) {
                alert(
                  'Sorry, an error occurred. Please check your recurrence rule text.',
                );
              }
            }}
          >
            Generate Occurrences
          </Button>

          <HeadingWrapper>
            <AdminPanelHeadingTwoText>Occurrences</AdminPanelHeadingTwoText>
            <Button variant="raised" onClick={onAddOccurrenceClick}>
              Add Occurrence
            </Button>
          </HeadingWrapper>

          {occurrences.map((occ, i) => (
            <EventOccurrence
              key={occ.key}
              startsAt={occ.startsAt}
              duration={occ.duration}
              onDeleteClick={() => dispatch(removeEventOccurrence(eventKey, i))}
              onStartsAtChange={startsAt =>
                dispatch(setEventOccurrenceStartsAt(eventKey, i, startsAt))
              }
              onDurationChange={duration =>
                dispatch(setEventOccurrenceDuration(eventKey, i, duration))
              }
            />
          ))}
        </AdminPanelMainForm>
      </AdminPanelBody>
    </AdminPanel>
  );
};

AdminEventPanel.propTypes = {
  eventKey: PropTypes.string.isRequired,
};
